<template>
  <textarea
    style="display: none"
    :id="code_edit_id"
  ></textarea>
</template>

<script lang="ts">
import { onMounted, onUnmounted, PropType, watch } from 'vue';
import { loadScript, loadStyle } from 'liyu-pc-base/utils/load-async-asset';
import { useEnv } from 'liyu-pc-base/init-plugin';
import expand from 'emmet';

type modeType = 'javascript' | 'htmlmixed' | 'css' | 'python' | 'jinja2' | 'sql' | 'xml';

// Dynamically set asset URLs based on the environment
const get_CODEMIRROR_BASE_URL = () => {
  const env = useEnv();
  return env.VUE_APP_PUBLIC_PATH + 'codemirror';
};

const loadedModes = new Set<string>();

async function loadMode(mode: modeType) {
  if (loadedModes.has(mode)) {
    return;
  }
  // The files copied from node_modules are not minified, so we use the standard names.
  let modeFile = '';
  switch (mode) {
    case 'javascript':
    case 'css':
    case 'htmlmixed':
    case 'python':
    case 'jinja2':
    case 'sql':
    case 'xml':
      modeFile = `${mode}/${mode}.js`;
      break;
  }
  if (modeFile) {
    await loadScript(`${get_CODEMIRROR_BASE_URL()}/mode/${modeFile}`);
    loadedModes.add(mode);
  }
}

function getWord(line: string, ch: number): [string, number] {
  const getNearTagChar = (str: string): string => {
    for (let i = str.length - 1; i > 0; i--) {
      if (str[i] === '>' || str[i] === '<') return str[i];
    }
    return str[0] || '<';
  };
  // 光标位于行末或单词末尾
  if (ch === line.length || (line.length > ch + 1 && (/S/.test(line[ch]) || line[ch] === '<'))) {
    let i;
    for (i = ch - 1; i >= 0; i--) {
      if (/S/.test(line[i]) || (line[i] === '>' && getNearTagChar(line.slice(0, i)) === '<')) {
        break;
      }
    }
    return [line.slice(i + 1, ch), i + 1];
  }
  return ['', 0];
}

function debounce(fn: any, delay = 1000) {
  let timer: any;
  return function (this: any) {
    const context = this;
    const args = arguments;
    clearTimeout(timer);
    timer = setTimeout(function () {
      fn.apply(context, args);
    }, delay);
  };
}

export default {
  name: 'CodeEditor',
  props: {
    editorValue: {
      type: String,
      default: '',
    },
    mode: {
      type: String as PropType<modeType>,
      required: true,
      default: 'javascript',
    },
    lineWrapping: {
      type: Boolean,
      default: true,
    },
    options: {
      type: Object,
      default: () => {
        return {
          lineNumbers: true,
          line: true,
          indentUnit: 2,
          lineWidth: 4,
          lineColor: '#b99944',
          immediate: true,
        };
      },
    },
  },
  setup(props: any, context: any) {
    let cmInstance: any;
    const code_edit_id: string = 'code_edit_id' + new Date().getTime();

    const initCodeMirror = async () => {
      const editor = document.getElementById(code_edit_id);
      let word: string, wordIndex: number;

      await loadMode(props.mode);

      const extraKeys = {
        'Ctrl-Space': 'autocomplete',
      };

      cmInstance = window.CodeMirror.fromTextArea(editor as HTMLTextAreaElement, {
        ...props.options,
        extraKeys,
        hintOptions: {
          // tables: {
          //   users: ['name', 'score', 'birthDate'],
          //   countries: ['name', 'population', 'size'],
          // },
        },
        lineWrapping: props.lineWrapping,
      });

      cmInstance.setOption('mode', props.mode);
      cmInstance.setOption('theme', 'material-darker');
      cmInstance.setValue(props.editorValue || '');

      cmInstance.on('change', (coder: any) => {
        if (props.mode === 'htmlmixed' || props.mode === 'css' || props.mode === 'xml') {
          // HTML模式下使用Emmet
          const { line: lineIndex, ch } = coder.getCursor();
          const line = coder.getLine(lineIndex);
          const wordResult = getWord(line, ch);
          word = wordResult[0];
          wordIndex = wordResult[1];
        }
        context.emit('update:editorValue', coder.getValue());
      });

      cmInstance.on(
        'change',
        debounce(() => {
          context.emit('debounce-update');
        }, 5000) as (instance: any) => void,
      );

      // Autocomplete on input
      cmInstance.on('inputRead', (cm: any, change: any) => {
        if (cm.state.completionActive || change.origin === '+delete' || change.origin === 'paste') {
          return;
        }
        // Show hint for normal typing
        const NON_SPECIAL_CHARS = /^[a-zA-Z0-9_.]*$/;
        if (NON_SPECIAL_CHARS.test(change.text[0])) {
          cm.showHint({ completeSingle: false });
        }
      });

      if (props.mode === 'htmlmixed' || props.mode === 'css' || props.mode === 'xml') {
        const currentExtraKeys = cmInstance.getOption('extraKeys');
        cmInstance.setOption('extraKeys', {
          ...currentExtraKeys,
          Tab: function (coder: any) {
            const indent = coder.getOption('indentUnit') || 2;
            const spaces = Array(indent + 1).join(' ');
            if (!word) {
              coder.replaceSelection(spaces);
            } else {
              try {
                const typeMap: any = {
                  htmlmixed: 'markup',
                  css: 'stylesheet',
                  xml: 'markup',
                };
                const emmet = expand(word, { type: typeMap[props.mode] });
                const { line, ch } = coder.getCursor();
                coder.setSelection({ line, ch }, { line, ch: wordIndex });
                const formatterEmmet = emmet
                  .split('\n')
                  .map((line: string, index: number) => {
                    if (index > 0) {
                      line = line.replace(/\t/g, spaces);
                      line = Array(wordIndex + 1).join(' ') + line;
                    }
                    return line;
                  })
                  .join('\n');
                coder.replaceSelection(formatterEmmet);
              } catch (e) {
                console.error(e);
                coder.replaceSelection(spaces);
              }
            }
          },
        });
      }
    };

    onMounted(async () => {
      try {
        if (!(window as any).CodeMirror) {
          // First time loading: fetch all assets in the correct order.
          await Promise.all([
            loadStyle(`${get_CODEMIRROR_BASE_URL()}/lib/codemirror.css`),
            loadStyle(`${get_CODEMIRROR_BASE_URL()}/theme/material-darker.css`),
            loadStyle(`${get_CODEMIRROR_BASE_URL()}/addon/hint/show-hint.css`),
          ]);
          await loadScript(`${get_CODEMIRROR_BASE_URL()}/lib/codemirror.js`);
          await Promise.all([
            loadScript(`${get_CODEMIRROR_BASE_URL()}/addon/hint/show-hint.js`),
            loadScript(`${get_CODEMIRROR_BASE_URL()}/addon/hint/anyword-hint.js`),
            loadScript(`${get_CODEMIRROR_BASE_URL()}/addon/hint/xml-hint.js`),
            loadScript(`${get_CODEMIRROR_BASE_URL()}/addon/hint/sql-hint.js`),
            loadScript(`${get_CODEMIRROR_BASE_URL()}/addon/hint/javascript-hint.js`),
            loadScript(`${get_CODEMIRROR_BASE_URL()}/addon/hint/css-hint.js`),
            loadScript(`${get_CODEMIRROR_BASE_URL()}/addon/hint/html-hint.js`),
          ]);
        }

        await initCodeMirror();

        await new Promise((resolve, reject) => {
          setTimeout(() => {
            resolve(null);
          }, 500);
        });
        if (cmInstance) {
          cmInstance.refresh();
        }
      } catch (error) {
        console.error('Failed to load CodeMirror assets:', error);
      }
    });

    onUnmounted(() => {
      if (cmInstance) {
        cmInstance.toTextArea();
        cmInstance = null;
      }
    });

    watch(
      () => props.mode,
      async val => {
        if (cmInstance) {
          await loadMode(val);
          cmInstance.setOption('mode', val);
        }
      },
    );

    watch(
      () => props.editorValue,
      async newValue => {
        if (cmInstance && newValue !== cmInstance.getValue()) {
          cmInstance.setValue(newValue || '');
        }
      },
    );

    return {
      code_edit_id,
      handleCodeMirrorRefresh() {
        if (cmInstance) {
          cmInstance.refresh();
        }
      },
    };
  },
};
</script>
<style>
.CodeMirror {
  height: 300px !important;
  font-size: 15px !important;
  font-family: Monaco, MonoSpace;
  line-height: 1.4 !important;
}

.CodeMirror-lines {
  padding: 4px 0;
  min-height: 1px; /* Ensure line measure div is not collapsed */
}

.CodeMirror-hints {
  position: absolute;
  z-index: 9999 !important;
  overflow: hidden;
  list-style: none;
  margin: 0 0 0 5px; /* Add a 5px offset to the left */
  padding: 2px;
  box-shadow: 2px 3px 5px rgba(0, 0, 0, 0.2);
  border-radius: 3px;
  border: 1px solid #555; /* A softer border for the theme */
  background: #2d2d2d;
  font-size: 100%;
  font-family: monospace;
  max-height: 20em;
  overflow-y: auto;
}

.CodeMirror-hint {
  margin: 0;
  padding: 0 4px;
  border-radius: 2px;
  white-space: pre;
  color: white;
  cursor: pointer;
}

li.CodeMirror-hint-active {
  background: #08f;
  color: white;
}
</style>
